import { bugsnagError } from 'services/bugsnag';
import warehouse from 'services/warehouse';
import * as CONSTANTS from './actions-constants';
import { PRODUCT_DISLIKE } from '../constants';

/**
 * Fetches all user favorite products from warehouse
 * and fires actions to populate productDislikeReasons &
 * productLikeStatuses in the state.
 */
export const fetchAllUserFavorites = () => async (dispatch) => {
  const responseData = await warehouse.fetchAllUserFavorites(1);
  const numberOfPages = responseData.num_pages || 1;

  // Update redux state with first page of results
  dispatch(updateStatusesAndDislikeReasons(responseData));

  // Iterate through all the pages of liked/disliked products
  for (let nextPage = 2; nextPage <= numberOfPages; nextPage++) {
    try {
      const responseData = await warehouse.fetchAllUserFavorites(nextPage);
      dispatch(updateStatusesAndDislikeReasons(responseData));
    } catch (error) {
      bugsnagError(error);
    }
  }
};

/**
* Helper functions for fetchAllUserFavorites.
*/

/**
* @param {Object} response warehouse.fetchAllUserFavorites()
*/
export const updateStatusesAndDislikeReasons = (response) => (dispatch) => {
  // Prepare data
  let likeStatuses = setLikeStatusesObject(response);
  let dislikeReasons = setDislikeReasonsObject(response);

  // Update redux state
  dispatch(updateProductLikeStatuses(likeStatuses));
  dispatch(updateProductDislikeReasons(dislikeReasons));
};

/**
* @param {Object} response   warehouse.fetchAllUserFavorites()
* @return {Object} productLikeStatuses
* A product like status [-1 or 1] is associated with a productId
* ex: productLikeStatuses = { productId1: -1, productId2: 1 }
*/
export const setLikeStatusesObject = (response) => {
  let productLikeStatuses = {};
  const favoriteProducts = response.results;

  favoriteProducts.forEach(favoriteProduct => {
    const productId = favoriteProduct.id;
    const likeStatus = favoriteProduct.like;

    productLikeStatuses[productId] = likeStatus;
  });
  return productLikeStatuses;
};

/**
* @param {Object} response   warehouse.fetchAllUserFavorites()
* @return {Object} productDislikeReasons
* ex: productDislikeReasons = { productId1: ["Not my style", "Wrong size"], productId2: ["Color"] }
*/
export const setDislikeReasonsObject = (response) => {
  let productDislikeReasons = {};
  const favoriteProducts = response.results;

  favoriteProducts.forEach(favoriteProduct => {
    const productId = favoriteProduct.id;
    const likeStatus = favoriteProduct.like;
    const reasonsOfDislike = favoriteProduct.reasons;

    if (likeStatus === PRODUCT_DISLIKE && reasonsOfDislike) {
      productDislikeReasons[productId] = reasonsOfDislike;
    }
  });
  return productDislikeReasons;
};

/**
 * Setter actions
 */

/**
 * @param {Object} statuses
 * ex: statuses = { productId1: -1, productId2: 1 }
 */
export const setAllLikeStatuses = (likeStatuses) => ({
  type: CONSTANTS.SET_ALL_LIKE_STATUSES,
  payload: likeStatuses
});

/**
 * @param {Object} dislikeReasons
 * ex: dislikeReasons = { productId1: ["Not my style", "Wrong size"], productId2: ["Color"] }
 */
export const setAllDislikeReasons = (dislikeReasons) => ({
  type: CONSTANTS.SET_ALL_DISLIKE_REASONS,
  payload: dislikeReasons
});

/**
 * Update actions
 */

/**
 * @param {Object} statuses
 * Like status is an integer -1 or 1.
* ex: statuses = { productId1: -1, productId2: 1 }
*/
export const updateProductLikeStatuses = (statuses) => ({
  type: CONSTANTS.UPDATE_PRODUCT_LIKE_STATUSES,
  payload: statuses
});

/**
* @param {Object} dislikeReasons
* ex: dislikeReasons = { productId1: ["Not my style", "Wrong size"], productId2: ["Color"] }
*/
export const updateProductDislikeReasons = (dislikeReasons) => ({
  type: CONSTANTS.UPDATE_PRODUCT_DISLIKE_REASONS,
  payload: dislikeReasons
});

/**
 * @param {Number} productId
 * Clears the dislike reasons of a given product.
*/
export const clearDislikeReasons = (productId) => ({
  type: CONSTANTS.CLEAR_DISLIKE_REASONS,
  payload: productId
});

/**
* Update methods
*/

/**
* @param {Number} productId
* @param {Number} likeStatus
*/
export const updateLikeStatuses = ({
  productId,
  likeStatus,
  source
}) => async (dispatch) => {
  // Update Redux store: updates productLikeStatus
  dispatch(updateProductLikeStatuses({ [productId]: likeStatus }));

  // Update Redux store: clears dislikeReasons (if likeStatus is neutral or liked)
  let reasons;
  if (likeStatus !== PRODUCT_DISLIKE) {
    reasons = [];
    dispatch(clearDislikeReasons(productId));
  }

  // Update server
  try {
    await warehouse.updateProductLikeStatus({ productId, likeStatus, reasons, source });
  } catch (error) {
    bugsnagError(error);
  }
};

/**
* @param {Number} productId
* @param {Array} reasons
*/
export const updateDislikeReasons = ({
  productId,
  reasons,
  source
}) => async (dispatch) => {
  // Update Redux store: updates dislikeReasons & likeStatus
  dispatch(updateProductDislikeReasons({ [productId]: reasons }));
  dispatch(updateProductLikeStatuses({ [productId]: PRODUCT_DISLIKE }));

  // Update server
  try {
    await warehouse.updateProductLikeStatus({ productId, likeStatus: PRODUCT_DISLIKE, reasons, source });
  } catch (error) {
    bugsnagError(error);
  }
};
